// --------------------------------------------------------------
// Created On: 2025-09-30
// Author: Zachary Thomas
//
// Last Modified: 2025-11-05
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import { MFA_EXPIRATION_TIME_MINUTES } from "src/constants/miscellaneous";
import Card from "../../../components/Card/Card";
import Error from "../../../components/Error/Error";
import styles from "./MfaLoginForm.module.scss";

// Form for entering a MFA code to complete user login.
export default function MfaLoginForm(props: Props): Component {
  // Check if the user pressed enter, if they did we attempt to login.
  function checkPressEnter(e: React.KeyboardEvent): void {
    if (e.key === "Enter") {
      props.onSubmit();
    }
  }

  return (
    <form className={styles.body} onSubmit={(e) => e.preventDefault()} onKeyDown={(e) => checkPressEnter(e)}>
      <Card title="Enter Authentication Code">
        <div className="px-3 px-lg-5 mb-4">
          <p className="mt-2">
            A text message with your authentication code has been sent to your registered phone number. Please enter the
            code to continue. Your authentication code will expire in {MFA_EXPIRATION_TIME_MINUTES} minutes.
          </p>

          {/* The pattern for this input is always numbers, but it is a string. For example '000349' is valid. */}
          <input
            data-test="mfa-code-input"
            className="form-control mt-2 mb-4"
            pattern="\d*"
            id="mfa-code-text"
            aria-describedby="MFA Code"
            placeholder="Enter authentication code..."
            value={props.mfaCode}
            disabled={props.disabled}
            onChange={(e) => props.onChangeMfaCode(e.target.value)}
          />

          <Error message={props.errorMessage} />

          <div className="my-4">
            <button
              data-test="cancel-mfa-login-button"
              type="submit"
              id="cancel-mfa-login"
              className="btn btn-secondary"
              disabled={props.disabled}
              onClick={() => props.onCancel()}
            >
              Return to Login
            </button>

            <button
              data-test="login-button"
              type="submit"
              id="submit-login"
              className="btn btn-success float-end"
              disabled={props.disabled}
              onClick={() => props.onSubmit()}
            >
              Submit Code
            </button>
          </div>
        </div>
      </Card>
    </form>
  );
}

interface Props {
  errorMessage: string;
  mfaCode: string;
  disabled?: boolean;
  onChangeMfaCode: (mfaCode: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}
